import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useUser } from '@/context/UserContext';
import axios from 'axios';
import { GraduationCap, Loader2, Users, Award, TrendingUp, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    id: 'honours',
    title: 'Honours in Pumpology',
    subtitle: 'GPA 3.50 — 4.00',
    description: 'The elite. These traders graduated at the top of the food chain.',
    icon: Award,
    borderColor: 'border-yellow-500/40',
    hoverBorder: 'hover:border-yellow-500/70',
    hoverShadow: 'hover:shadow-[0_0_30px_rgba(234,179,8,0.2)]',
    titleColor: 'text-yellow-500',
    accentBg: 'bg-yellow-500/10',
    badgeClass: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
    filter: (gpa) => gpa >= 3.5,
  },
  {
    id: 'trader',
    title: 'Trader I Guess',
    subtitle: 'GPA 3.00 — 3.49',
    description: 'Respectable. Not amazing, not embarrassing. You passed.',
    icon: TrendingUp,
    borderColor: 'border-pump-green/30',
    hoverBorder: 'hover:border-pump-green/50',
    hoverShadow: 'hover:shadow-[0_0_30px_rgba(57,255,20,0.15)]',
    titleColor: 'text-pump-green',
    accentBg: 'bg-pump-green/10',
    badgeClass: 'bg-pump-green/20 text-pump-green border-pump-green/30',
    filter: (gpa) => gpa >= 3.0 && gpa < 3.5,
  },
  {
    id: 'near-retard',
    title: 'Damn Near Retard',
    subtitle: 'GPA 2.40 — 2.99',
    description: 'You barely made it. The letters are rearranged... loosely.',
    icon: AlertTriangle,
    borderColor: 'border-red-500/30',
    hoverBorder: 'hover:border-red-500/50',
    hoverShadow: 'hover:shadow-[0_0_30px_rgba(239,68,68,0.15)]',
    titleColor: 'text-red-500',
    accentBg: 'bg-red-500/10',
    badgeClass: 'bg-red-500/20 text-red-500 border-red-500/30',
    filter: (gpa) => gpa < 3.0,
  },
];

const CertificateCard = ({ grad, tier, idx }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: idx * 0.05 }}
  >
    <Link
      to={`/wall-of-traders/${grad.certificate_id}`}
      className="block group"
      data-testid={`graduate-card-${tier.id}-${idx}`}
    >
      <div className={`rounded-2xl overflow-hidden border ${tier.borderColor} ${tier.hoverBorder} transition-all duration-300 ${tier.hoverShadow}`}>
        <div className="relative">
          <img
            src={grad.certificate_image}
            alt={`${grad.student_name}'s Certificate`}
            className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-300"
          />
          <div className="absolute inset-0 flex flex-col items-center pointer-events-none">
            <div className="absolute w-full text-center" style={{ top: '40%' }}>
              <p
                className="font-serif italic text-gray-800 px-4"
                style={{
                  fontSize: 'clamp(10px, 2.5vw, 20px)',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  letterSpacing: '2px',
                  fontWeight: '500'
                }}
              >
                {grad.student_name}
              </p>
            </div>
            <div className="absolute w-full text-center" style={{ top: '54%' }}>
              <p
                className="font-serif italic text-gray-800"
                style={{
                  fontSize: 'clamp(8px, 2vw, 16px)',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  letterSpacing: '1px',
                  fontWeight: '500'
                }}
              >
                {grad.gpa?.toFixed(2)}
              </p>
            </div>
            <div className="absolute" style={{ top: '63.5%', left: '58%' }}>
              <p
                className="font-serif text-gray-700"
                style={{
                  fontSize: 'clamp(6px, 1.2vw, 10px)',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontStyle: 'italic'
                }}
              >
                {grad.issue_date && new Date(grad.issue_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Link>
  </motion.div>
);

const TierSection = ({ tier, graduates }) => {
  const Icon = tier.icon;
  const filtered = graduates
    .filter(g => tier.filter(g.gpa || 0))
    .sort((a, b) => (b.gpa || 0) - (a.gpa || 0));

  if (filtered.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-16"
      data-testid={`tier-section-${tier.id}`}
    >
      {/* Tier Header */}
      <div className="flex items-center gap-4 mb-2">
        <div className={`w-12 h-12 rounded-xl ${tier.accentBg} flex items-center justify-center`}>
          <Icon className={tier.titleColor} size={24} />
        </div>
        <div>
          <h2 className={`font-unbounded font-bold text-2xl ${tier.titleColor}`}>
            {tier.title}
          </h2>
          <p className="text-zinc-500 text-sm font-mono">{tier.subtitle}</p>
        </div>
        <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium border ${tier.badgeClass}`}>
          {filtered.length} graduate{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>
      <p className="text-zinc-500 text-sm mb-6 ml-16">{tier.description}</p>

      {/* Graduates Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((grad, idx) => (
          <CertificateCard key={grad.certificate_id} grad={grad} tier={tier} idx={idx} />
        ))}
      </div>
    </motion.div>
  );
};

const WallOfTradersPage = () => {
  const { user } = useUser();
  const [graduates, setGraduates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGraduates = async () => {
      try {
        const res = await axios.get(`${API}/graduates`);
        setGraduates(res.data);
      } catch (err) {
        console.error('Failed to load graduates:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGraduates();
  }, []);

  const isGraduated = user?.progress && ['memeglish', 'memethematics', 'memestory', 'memeart'].every(
    c => user.progress[c]?.quiz_passed
  );

  if (!isGraduated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <GraduationCap className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="font-unbounded font-bold text-2xl text-white mb-2">Wall of Traders</h2>
          <p className="text-zinc-400">Complete all 4 courses to unlock the Wall of Traders</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-degen-purple animate-spin" />
      </div>
    );
  }

  const hasAnyGraduates = graduates.length > 0;

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Users className="text-pump-green" size={32} />
            <h1 className="font-unbounded font-black text-4xl sm:text-5xl text-white">
              WALL OF <span className="text-pump-green">TRADERS</span>
            </h1>
          </div>
          <p className="text-zinc-400 text-base max-w-lg mx-auto">
            Every retard who rearranged their letters. Verified Bachelors of Pumpology.
          </p>
          <p className="text-zinc-600 text-sm mt-2 font-mono">{graduates.length} graduate{graduates.length !== 1 ? 's' : ''}</p>
        </motion.div>

        {!hasAnyGraduates ? (
          <div className="text-center py-16">
            <p className="text-zinc-500">No graduates yet. Be the first!</p>
          </div>
        ) : (
          TIERS.map(tier => (
            <TierSection key={tier.id} tier={tier} graduates={graduates} />
          ))
        )}
      </div>
    </div>
  );
};

export default WallOfTradersPage;
